import React, { Fragment, useMemo, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';

import { getCountryName } from '../utils/index';

import { can } from '../can';
import { routes } from '../routes';

import { reportFrequencies } from '../constants';

import { exportSpreadsheet } from '../utils/exportSpreadsheet';
import { sizeToString } from '../utils';
import { groupBy } from 'lodash';
import DoorList from '../components/DoorList';
import {
  orderTypes,
  orderStatusLabel,
  orderStatusColors,
  modeLabels,
  lockerStatusLabels,
  doorTypeLabels,
  modes,
} from '../constants';

import api from '../utils/api';
import { useForm } from '../utils/form';
import { roles } from '../constants';

import { Loading, NotFound, Forbidden } from './PageStatus';
import PageHeader from '../components/common/PageHeader';
import { List, Header, Body, Row, Cell } from '../components/common/List';
import { FiArrowUpRight, FiLoader } from 'react-icons/fi';

import TextArea from '../components/common/TextArea';
import Input from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';

import Select, { indexToOptions, objectToOptions } from '../components/common/Select';

import Label, { labelTypes } from '../components/common/Label';
import ConnectionIndicator from '../components/common/ConnectionIndicator';
import Count from '../components/graphs/Count';
import Link from '../components/common/Link';

import grid from '../styles/grid.css';
import css from './Locker.css';

const momentToISO = (d) => d.toISOString(true).substring(0, 16) + 'Z';

const devicesToOptions = (devices) =>
  devices.map((d) => ({
    value: d.id,
    label: "ID" + d.id + ' (' + d.deviceType + ')',
  }));

const createReportPresets = () => {
  const presets = [
    {
      name: 'custom',
      label: `Rango personalizado`,
    },
    {
      label: 'thisWeek',
      name: 'thisWeek',
      label: `Esta semana (hasta ayer)`,
      isDisabled: moment().weekday() === 0,
      timeFrom: moment().startOf('isoWeek'),
      timeTo: moment().startOf('day'),
    },
    {
      name: 'lastWeek',
      label: `Semana pasada`,
      timeFrom: moment().subtract(1, 'weeks').startOf('isoWeek'),
      timeTo: moment().subtract(1, 'weeks').endOf('isoWeek'),
    },
    {
      name: 'thisMonth',
      label: `Este mes (${moment().startOf('month').format('DD MMMM')} - ayer)`,
      isDisabled: moment().date() === 1,
      timeFrom: moment().startOf('month'),
      timeTo: moment().startOf('day'),
    },
    {
      name: 'lastMonth',
      label: `Mes pasado (${moment().subtract(1, 'months').format('MMMM')})`,
      timeFrom: moment().subtract(1, 'months').startOf('month'),
      timeTo: moment().subtract(1, 'months').endOf('month'),
    },
    {
      name: 'twoMonthsAgo',
      label: moment().subtract(2, 'months').format('MMMM'),
      timeFrom: moment().subtract(2, 'months').startOf('month'),
      timeTo: moment().subtract(2, 'months').endOf('month'),
    },
    {
      name: 'threeMonthsAgo',
      label: moment().subtract(3, 'months').format('MMMM'),
      timeFrom: moment().subtract(3, 'months').startOf('month'),
      timeTo: moment().subtract(3, 'months').endOf('month'),
    },
  ];

  return presets;
};

const presetsToOptions = (presets) =>
  presets.map((p) => ({
    value: p.name,
    label: p.label,
    isDisabled: p.isDisabled,
  }));

const timezones = {
  'America/Santiago': {
    utc: 'UTC-4',
    offset: '-03:00',
    value: 'America/Santiago',
    label: 'Santiago (UTC-4)',
  },
  'America/Punta_Arenas': {
    utc: 'UTC-3',
    offset: '-03:00',
    value: 'America/Punta_Arenas',
    label: 'Punta Arenas (UTC-3)',
  },
};

const reportIndexFields = {
  type: ['text', false, { initialValue: 'devices' }],
  frequency: ['text', true, { initialValue: 'day' }],
  preset: ['text', true],
  timeFrom: [
    'date',
    false,
    {
      required: (v, values) => values.get('preset') == 'custom',
      validate: (v, values) => {
        if (moment().isBefore(v)) return 'No puede estar en el futuro';
        if (!values.get('timeTo')) return null;
        const diff = moment(values.get('timeTo')).diff(v, 'days');
        if (diff < 0) return 'Debe ser anterior al término';
        if (diff > 93) return 'El rango máximo permitido es 3 meses';
        return null;
      },
    },
  ],
  timeTo: [
    'date',
    false,
    {
      required: (v, values) => values.get('preset') == 'custom',
      validate: (v, values) => {
        if (moment().isBefore(v)) return 'Fecha no puede estar en el futuro';
        if (!values.get('timeFrom')) return null;
        const diff = moment(v).diff(values.get('timeFrom'), 'days');
        if (diff < 0) return 'Debe ser posterior al inicio';
        if (diff > 93) return 'El rango máximo permitido es 3 meses';
        return null;
      },
    },
  ],
  timezone: ['text', true, { initialValue: moment.tz.guess() }],
  deviceId: ['number', true],
};

const lockerFields = {
  name: ['text', true],
  locationId: ['number', true],
  status: ['text'],
  extras: ['json'],
  preferences: {
    mode: ['text'],
    doorType: ['text'],
    localBrokerIp: ['ip'],
    custodyWebsite: ['url'],
    custodyEnablePartialPickup: ['toggle'],
    custodyEnableResidentToken: ['toggle'],
    custodyPaymentsEnabled: ['toggle'],
    pickupNationalIdEnabled: ['toggle'],
    pickupNationalIdCheckEnabled: ['toggle'],
    courierPickupCodeRequired: ['toggle'],
    clientAdminControlDoorsEnabled: ['toggle'],
    enableUnlimitedReservations: ['toggle'],
    enableMultiDropCodes: ['toggle'],
    enableOfflinePickup: ['toggle'],
    enableScreenLogs: ['toggle'],
    enableDualLocker: ['toggle'],
    forceNumericKeypadEntry: ['toggle'],
    forceOnlyScanNationalId: ['toggle'],
    customizeColors: ['toggle'],
    customizeText: ['toggle'],
    name: ['text'],
    logo: ['text'], // logo left
    logoRight: ['text'],
    background: ['text'],
    logoRatio: ['text'],
    supportPhone: ['text'],
    supportEmail: ['text'],
    homePretitle: ['text'],
    homeTitle: ['text'],
    homeInstruction: ['text'],
    btnManualEntry: ['text'],
    help: ['text'],
    btnLogin: ['text'],
    pickupPrompt: ['text'],
    pickupScanPrompt: ['text'],
    nationalIdPromptPretitle: ['text'],
    nationalIdPropmtTitle: ['text'],
    openDoors: ['text'],
    supportTitle: ['text'],
    supportBody: ['text'],
    packageName: ['text'],
    offlineTitle: ['text'],
    offlineBody: ['text'],
    maintenanceTitle: ['text'],
    maintenanceBody: ['text'],
    outOfServiceTitle: ['text'],
    outOfServiceBody: ['text'],
    frozenTitle: ['text'],
    frozenBody: ['text'],
    thankyou: ['text'],
    wrongCode: ['text'],
    backgroundColor: ['color'],
    textColor: ['color'],
    adminTextColor: ['color'],
    buttonColor: ['color'],
    buttonTextColor: ['color'],
    buttonActiveColor: ['color'],
    buttonBorderColor: ['color'],
    adminBackgroundColor: ['color'],
    primaryButtonBackground: ['color'],
    maskedIndexDoorNumber: ['number'],
    scannerArrowFillColor: ['color'],
    scannerFillColor: ['color'],
    scannerCodeFillColor: ['color'],
    scannerBackgroundFill: ['color'],
    scanNationalIdOrManualEntry: ['toggle'],
    enableIncongruencesCheck: ['toggle'],
  },
};

export const Locker = ({ match, history }) => {
  // Call getDeviceData once on page init for today's range
  const [resumenTable, setResumenTable] = useState([]);
  const [lockerOrders, setLockerOrders] = useState([]);
  const [lockerOrdersLoaded, setLockerOrdersLoaded] = useState(false);
  React.useEffect(() => {
    const fetchDeviceData = async () => {
      if (item.loaded && data.devices && data.devices.length > 0) {
        const today = moment();
        // from 3 days ago
        let timeFrom = today.startOf('day').toISOString();

        timeFrom = moment(timeFrom).subtract(3, 'days').toISOString();

        const timeTo = today.endOf('day').toISOString();
        const actualData = await actions.getDeviceData({
          timeFrom,
          timeTo,
          frequency: reportFrequencies('day'),
          timezone: moment.tz.guess(),
        });

        const resumenRows = [];
        Object.keys(actualData.sourceMap).forEach((source) => {
          const records = actualData.sourceMap[source];
          if (records.length > 0 && source.includes('-resumen')) {
            records.forEach(resumenParams => {

              // format to time part of the lastRecordTime 
              const time = resumenParams["lastRecordTime"].split("T")[1].split("Z")[0];

              resumenRows.push({
                source: resumenParams["Fuente"],
                day: resumenParams["Día"],
                hour: time,
                lastRecordTime: resumenParams["lastRecordTime"],
                lastTemperature: resumenParams["lastTemperature"],
                lastHumidity: resumenParams["lastHumidity"],
              });
            });
          }
        });
        setResumenTable(resumenRows);
        console.log("resumenTable", resumenRows);
      }
    };
    fetchDeviceData();
    // Only run once on mount
    // eslint-disable-next-line
  }, []);
  const { id } = match.params;
  const { user } = api.auth();

  const { item, actions } = api.lockers.item(id, {}, { history });
  const { index: locationsIndex, actions: locationsActions } = api.locations.index(null, { lazy: true });

  const { actions: ordersActions } = api.orders.index(null, { lazy: true });

  React.useEffect(() => {
    let isMounted = true;

    const getLockerOrders = async () => {
      setLockerOrdersLoaded(false);
      const allOrders = await ordersActions.getAll({ lockerId: id, status: '!completed' });

      if (!isMounted) return;

      setLockerOrders(allOrders ? allOrders.toJS() : []);
      setLockerOrdersLoaded(true);
    };

    getLockerOrders();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const form = useForm(lockerFields, item.data, { submit: actions.update });
  const [modal, setModal] = useState();
  const reportForm = useForm(reportIndexFields);
  const reportPresets = useMemo(createReportPresets, []);

  const getLocations = async (search) => {
    const index = await locationsActions.get({ search, perPage: 50 });
    return indexToOptions(index, { value: 'id', label: 'name' });
  };

  const onDelete = async () => {
    await actions.delete();
  };

  const doorsBySize = useMemo(() => {
    if (!item.loaded) return;
    const data = item.data.toJS();
    const unorderedDoors = groupBy(
      data.doors.map((curr) => {
        curr.size = sizeToString(curr);
        return curr;
      }),
      'size'
    );
    const doorsBySize = {};
    Object.keys(unorderedDoors)
      .sort((a, b) => {
        let a_ = a
          .replace(/ /g, '')
          .split('×')
          .map((e) => parseInt(e));
        let b_ = b
          .replace(/ /g, '')
          .split('×')
          .map((e) => parseInt(e));
        for (let i = 0; i < a_.length; i++) {
          if (a_[i] < b_[i]) {
            return -1;
          } else if (a_[i] > b_[i]) {
            return 1;
          }
        }
      })
      .forEach(function (key) {
        doorsBySize[key] = unorderedDoors[key];
      });
    return doorsBySize;
  }, [item.data]);

  if (!can(user, 'show', 'lockers')) return <Forbidden />;

  if (item.error && item.status == 404) return <NotFound />;
  if (!item.loaded) return <Loading />;

  const data = item.data.toJS();
  const { devices } = data;

  const modeOptions = objectToOptions(modeLabels.obj);
  const doorTypeOptions = objectToOptions(doorTypeLabels.obj);
  const types = [
    doorTypeLabels('serial'),
    doorTypeLabels('serialV2'),
    doorTypeLabels('maletekV3'),
  ];
  const lockerStatusOptions = objectToOptions(lockerStatusLabels.obj, { disabled: ['unregistered'] });

  const isUnregistered = data.status == 'unregistered';
  const isOffline = !isUnregistered && !data.online;
  const country = getCountryName(data.locationCountryCode);
  const canUpdate = can(user, 'update', 'lockers');
  const readOnly = isOffline || !canUpdate;
  const totalOrders = lockerOrders.length;
  const totalReservedOrders = lockerOrders.filter((order) => order.status === 'reserved').length;
  const totalReadyOrders = lockerOrders.filter((order) => order.status === 'ready').length;
  const totalRevokedOrders = lockerOrders.filter((order) => order.orderType === 'reversal').length;
  const isDualLockerEnabled = !!form.getValue(['preferences', 'enableDualLocker']);
  const isEvenDoorCount = data.doors.length % 2 === 0;
  const canEnableDualLocker = isEvenDoorCount;
  const getActive = (arr) =>
    arr.reduce((acc, curr) => (acc = curr.status == 'active' && !curr.reserved && !curr.occupied ? acc + 1 : acc), 0);

  const getSize = (n) => {
    if (n == 0) return 'S';
    else if (n == 1) return 'M';
    else if (n == 2) return 'L';
    else {
      let size = '';
      for (let i = 0; i < n - 2; i++) size = size + 'X';
      return size + 'L';
    }
  };

  const createReport = async () => {
    const values = reportForm.getValues().toJS();
    const preset = { ...reportPresets.find((p) => p.name == values.preset) };

    if (preset.name == 'custom') {
      preset.timeFrom = moment(values.timeFrom).startOf('day');
      preset.timeTo = moment(values.timeTo).endOf('day');
    }

    console.log("value....", values)
    const reportParams = {
      frequency: reportFrequencies('day'),
      timeFrom: momentToISO(preset.timeFrom).replace('Z', timezones[values.timezone].offset),
      timeTo: momentToISO(preset.timeTo).replace('Z', timezones[values.timezone].offset),
      timezone: timezones[values.timezone]
    };

    if (reportForm.validate()) {
      exportXls(reportParams);
    }
  };

  const exportXls = async (reportParams) => {
    const { frequency, timeFrom, timeTo } = reportParams;
    const res = await actions.getDeviceData(reportParams);
    if (!res) return;

    if (!res.sourceMap || Object.keys(res.sourceMap).length === 0) {
      return alert('No hay datos disponibles para el período seleccionado.');
    }

    const { id, deviceType } = res.device;

    exportSpreadsheet("deviceData", res.sourceMap, {
      filename: `${data.name}-device ID${id}-${deviceType}-${timeFrom}-${timeTo}`,
      extension: 'xls',
    });
  };

  const openModal = () => {
    setModal(true);
  };

  if (item.status == 404) return <NotFound />;
  if (!item.loaded) return <Loading />;

  return (
    <main className={css.root}>
      <PageHeader
        section="lockers"
        title={form.getValue('name')}
        dirty={form.dirty && !isOffline}
        autoSave={!isOffline && canUpdate}
        {...item}
        subtitle={
          <Fragment>
            <strong>ID #{data.id}</strong> |{' '}
            {isUnregistered ? (
              ' No registrado'
            ) : (
              <>
                <ConnectionIndicator online={data.online} />
                {isOffline && data.lastPingAt ? (
                  <span>
                    {' '}
                    Última conexión: {moment(data.lastPingAt).fromNow()} ({moment(data.lastPingAt).format('DD/MM/YYYY HH:mm')})
                  </span>
                ) : null}
              </>
            )}{' '}
            | {data.locationName}{' '}
            {country ? `| ${country}` : ''}
            {isOffline && canUpdate && (
              <div>
                <i>No es posible hacer cambios cuando el locker está offline.</i>
              </div>
            )}
          </Fragment>
        }>
        {can(user, 'delete', 'lockers') && (
          <Button confirm="¿Eliminar este Locker?" destructive onClick={onDelete}>
            Eliminar
          </Button>
        )}

        {devices && devices.length > 0 && (user.role === roles('client') || user.role === roles('admin') || user.role === roles('manager')) && (
          <Button className={css.button} onClick={openModal}>
            Reporte T/H
          </Button>
        )}

      </PageHeader>
      <Modal open={modal} onClose={() => setModal(false)}>
        {can(user, 'create', 'reports') && (
          <div>
            <h3>Nuevo Reporte</h3>
            <Select
              className={css.select}
              label="Dispositivo"
              name="deviceId"
              form={reportForm}
              options={devicesToOptions(devices)}
            />
            <Select
              className={css.select}
              isClearable={false}
              name="preset"
              form={reportForm}
              label="Período"
              options={presetsToOptions(reportPresets)}
            />
            {reportForm.getValue('preset') == 'custom' && (
              <Fragment>
                <Input label="Fecha Incio" name={'timeFrom'} form={reportForm} />
                <Input label="Fecha Término" name={'timeTo'} form={reportForm} />
              </Fragment>
            )}
            {/*             <Select
              className={css.select}
              isClearable={false}
              name="timezone"
              form={reportForm}
              label="Zona Horaria"
              options={Object.values(timezones)}
            /> */}
            <Button
              className={css.button}
              onClick={createReport}
              disabled={!reportForm.getValue('preset') || !reportForm.getValue('deviceId')}>
              Crear
            </Button>

            {/* Resumen Table */}
            {resumenTable.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4>Resumen T/H</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em' }}>
                  <thead>
                    <tr>
                      <th style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>Fuente</th>
                      <th style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>Día</th>
                      <th style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>Hora</th>
                      <th style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>T</th>
                      <th style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>H</th>

                    </tr>
                  </thead>
                  <tbody>
                    {resumenTable.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ borderBottom: '1px solid #eee', padding: '4px' }}>{row.source}</td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '4px' }}>{row.day}</td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '4px' }}>{row.hour}</td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '4px' }}>{row.lastTemperature}</td>
                        <td style={{ borderBottom: '1px solid #eee', padding: '4px' }}>{row.lastHumidity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>
      <div className={grid.grid}>
        <section className={grid.half}>
          <div className={classnames(css.formPanel, css.ordersPanel)}>
            <h2 className={css.panelTitle}>Órdenes activas en este locker</h2>
            <div className={css.ordersSummary}>
              <span>
                Total: <strong>{lockerOrdersLoaded ? totalOrders : '...'}</strong>
              </span>
              <span>
                Reservadas: <strong>{lockerOrdersLoaded ? totalReservedOrders : '...'}</strong>
              </span>
              <span>
                Listas: <strong>{lockerOrdersLoaded ? totalReadyOrders : '...'}</strong>
              </span>
              <span>
                Revocadas: <strong>{lockerOrdersLoaded ? totalRevokedOrders : '...'}</strong>
              </span>
            </div>
            {isUnregistered ? (
              <p className={css.infoText}>
                Una vez que el locker esté activo, se mostrarán las órdenes activas asociadas a este locker.
              </p>
            ) : (
              <div className={classnames({ [css.ordersScrollable]: lockerOrdersLoaded && lockerOrders.length > 50 })}>
                <List className={css.ordersList}>
                <Header>
                  <Cell head>ID</Cell>
                  <Cell head>Destinatario</Cell>
                  <Cell head>Bulto</Cell>
                  <Cell head>Puerta</Cell>
                  <Cell head>Estado</Cell>
                  <Cell head>Ver</Cell>
                </Header>
                {!lockerOrdersLoaded ? (
                  <Body>
                    <Row>
                      <Cell colSpan={6}>
                        <div className={css.loadingState}>
                          <FiLoader className={css.loadingIcon} />
                          <span>Cargando órdenes activas...</span>
                        </div>
                      </Cell>
                    </Row>
                  </Body>
                ) : lockerOrders.length ? (
                  <Body>
                    {lockerOrders.map((order, orderIndex) => {
                      const doorNumber = data.doors.find((door) => door.id == order.doorId)?.doorNumber;
                      const to = routes('order', { id: order.id });
                      const orderStatusText = orderStatusLabel(order);
                      const statusWithReservedTime =
                        order.status === 'reserved' && order.reservedAt && order.orderType !== 'return'
                          ? `${orderStatusText} (${moment(order.reservedAt).fromNow()})`
                          : orderStatusText;

                      return (
                        <Row key={`order-${orderIndex}`}>
                          <Cell className={css.orderId}>{order.id}</Cell>
                          <Cell className={css.orderRecipient}>{order.pickupName}</Cell>
                          <Cell className={css.orderBulk}>{sizeToString(order)}</Cell>
                          {/*
                           * XXX Here we should use the upcoming datapoint that shows if the door is taken.
                           */}
                          <Cell center className={css.orderDoor}>
                            {doorNumber ? doorNumber : <span title="Se asignará al entregar el paquete">–</span>}
                          </Cell>
                          <Cell className={css.orderStatus} style={{ color: orderStatusColors(order) }}>
                            <strong>{orderTypes(order.orderType)}: </strong>
                            {order.error ? (
                              <Label max={200} type={labelTypes('error')}>
                                {order.error}
                              </Label>
                            ) : (
                              statusWithReservedTime
                            )}
                          </Cell>
                          <Cell className={css.orderLinkCell} center>
                            <Link to={to} inline className={css.orderLinkIcon} tooltip={`Ver orden #${order.id}`}>
                              <FiArrowUpRight />
                            </Link>
                          </Cell>
                        </Row>
                      );
                    })}
                  </Body>
                ) : (
                  <Body>
                    <Row>
                      <Cell colSpan={6}>No hay órdenes activas asociadas a este locker.</Cell>
                    </Row>
                  </Body>
                )}
                </List>
              </div>
            )}
          </div>

          {can(user, 'update', 'lockers') ? (
            <div className={css.formPanel}>
              <h2 className={css.panelTitle}>Configuración</h2>
              <Select
                readOnly={readOnly || isUnregistered}
                name="status"
                isClearable={false}
                options={lockerStatusOptions}
                label="Estado"
                form={form}
                note={isUnregistered ? 'Solo puedes cambiar el estado luego de haber sido registrado.' : ''}
                placeholder="..."></Select>
              <Input readOnly={readOnly} name="name" label="Nombre" form={form} placeholder="Nombre del Locker" />
              <Select
                readOnly={readOnly}
                name="locationId"
                label="Ubicación"
                isClearable={false}
                form={form}
                loadOptions={getLocations}
                placeholder="No asignada"
              />

              <h2 className={css.panelTitle}>Preferencias</h2>

              <Select
                readOnly={readOnly}
                name={['preferences', 'mode']}
                isClearable={false}
                options={modeOptions}
                label="Modo"
                form={form}
                note={'Define el modo en el que opera el locker.'}
                placeholder="..."
              />

              {user.role === roles('admin') && (
                <Toggle
                  readOnly={readOnly}
                  name={['preferences', 'enableIncongruencesCheck']}
                  label="Chequeo de incongruencias habilitado."
                  form={form}
                  note={'No activar si el locker no está actualizado, esta opción puede generar problemas de funcionamiento si el locker tiene una versión antigua del software.'}
                />
              )}

              {modes("custody") === form.getValue(['preferences', 'mode'])
                ? (
                  <>
                    <Input
                      readOnly={readOnly}
                      name={['preferences', 'custodyWebsite']}
                      label="Sitio web custodia"
                      form={form}
                      placeholder={''}
                      note={'Sitio web aplicación para el modo custodia.'}
                    />
                    <Toggle
                      readOnly={readOnly}
                      name={['preferences', 'custodyEnablePartialPickup']}
                      label="Habilitar retiro parcial"
                      form={form}
                      note={'Permite retiros parciales en modo custodia.'}
                    />
                    <Toggle
                      readOnly={readOnly}
                      name={['preferences', 'custodyEnableResidentToken']}
                      label="Habilitar token de residente"
                      form={form}
                      note={'Permite el uso de token de residente en modo custodia.'}
                    />
                    <Toggle
                      readOnly={readOnly}
                      name={['preferences', 'custodyPaymentsEnabled']}
                      label="Habilitar pagos en custodia"
                      form={form}
                      note={'Habilita la funcionalidad de pagos cuando el locker opera en modo custodia.'}
                    />
                  </>
                ) : null}
              <Select
                readOnly={readOnly}
                name={['preferences', 'doorType']}
                isClearable={false}
                options={doorTypeOptions}
                label="Tipo de puerta"
                form={form}
                note={'Define el protocolo de comunicación con las puertas.'}
                placeholder="..."
              />

              {types
                .includes(form.getValue(['preferences', 'doorType'])
                ) ? (
                <Input
                  readOnly={readOnly}
                  name={['preferences', 'localBrokerIp']}
                  label="Ip Broker local"
                  form={form}
                  placeholder={'127.0.0.1'}
                  note={'Dirección IP del broker de comunicación MQTT para las puertas.'}
                />
              ) : null}

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'pickupNationalIdEnabled']}
                label="Solicitar identificación al retirar"
                form={form}
              />
              <Toggle
                readOnly={readOnly}
                name={['preferences', 'pickupNationalIdCheckEnabled']}
                label="Chequear identificación con RUT de la orden al retirar"
                form={form}
              />
              <Toggle
                readOnly={readOnly}
                name={['preferences', 'courierPickupCodeRequired']}
                label="Requerir código para retirar revocación"
                form={form}
              />
              <Toggle
                readOnly={readOnly}
                name={['preferences', 'clientAdminControlDoorsEnabled']}
                label="Habilitar control de puertas para cliente"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'enableUnlimitedReservations']}
                label="Habilitar reservas ilimitadas"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'enableMultiDropCodes']}
                label="Habilitar multiples codigos de entrega"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'enableOfflinePickup']}
                label="Habilitar retiro offline"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'enableScreenLogs']}
                label="Habilitar logs en pantalla"
                form={form}
              />

              <Toggle
                readOnly={readOnly || (!canEnableDualLocker && !isDualLockerEnabled)}
                name={['preferences', 'enableDualLocker']}
                label="Habilitar dual locker"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'forceNumericKeypadEntry']}
                label="Forzar ingreso numérico en teclado"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'scanNationalIdOrManualEntry']}
                label="Permitir escanear identificación o ingreso manual en retiro"
                form={form}
              />

              <Toggle
                readOnly={readOnly}
                name={['preferences', 'forceOnlyScanNationalId']}
                label="Forzar solo escaneo de identificación nacional"
                form={form}
              />

              <Input
                readOnly={readOnly}
                name={['preferences', 'maskedIndexDoorNumber']}
                label="Número de puerta índice enmascarado"
                form={form}
                placeholder={0}
                min={0}
                note="Número base que se sumará al número de puerta real para mostrar en la pantalla (ej: si es 100, la puerta 1 se mostrará como 101)"
              />

              <h2 className={css.panelTitle}>Personalización</h2>
              <Input
                readOnly={readOnly}
                name={['preferences', 'name']}
                label="Nombre"
                form={form}
                placeholder={'Maletek'}
              />
              <Input
                readOnly={readOnly}
                name={['preferences', 'logo']}
                label="Logo (url)"
                form={form}
                note={'Formatos permitidos: svg o png'}
                placeholder={'Sin logo'}
              />
              <Input
                readOnly={readOnly}
                name={['preferences', 'logoRight']}
                label="Logo derecho (url)"
                form={form}
                note={'Formatos permitidos: svg o png'}
                placeholder={'Sin logo'}
              />
              <Input
                readOnly={readOnly}
                name={['preferences', 'logoRatio']}
                label="Proporción del logo"
                form={form}
                placeholder={1.5}
                min={0.2}
                max={3}
                step={0.01}
                note="Ayuda a determinar el tamaño en el que se mostrará el logotipo en la pantalla"
              />
              <Input
                readOnly={readOnly}
                name={['preferences', 'background']}
                label="Fondo de pantalla de inicio (url)"
                form={form}
                note={'Formatos permitidos: svg o png'}
                placeholder={'Sin imagen de fondo'}
              />
              <h3 className={css.panelSubtitle}>Textos</h3>
              <Toggle
                readOnly={readOnly}
                name={['preferences', 'customizeText']}
                label="Personalizar textos?"
                form={form}
              />

              {form.getValue(['preferences', 'customizeText']) ? (
                <Fragment>
                  <p>
                    <small>
                      Puedes referir otras variables con brackets: <code>{'{{ > variable }}'}</code>, y utilizar tags de
                      html como <code>{'<br/>'}</code>. Los valores en blanco mantendrán el texto por defecto (en gris).
                    </small>
                  </p>
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'supportPhone']}
                    label="Teléfono de soporte (`supportPhone`)"
                    placeholder="+56 2 2964 2430"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'supportEmail']}
                    label="Email de Soporte(`supportEmail`)"
                    placeholder="soporte@maletek.cl"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'homePretitle']}
                    label="Pre-título de bienvenida (`homePretitle`)"
                    form={form}
                    placeholder="Retirando un paquete?"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'homeTitle']}
                    label="Título de bienvenida (`homeTitle`)"
                    form={form}
                    placeholder="Escanea tu código en <br/>el lector"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'homeInstruction']}
                    label="Instrucción página de bienvenida (`homeInstruction`)"
                    form={form}
                    placeholder=""
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'btnManualEntry']}
                    label='Botón "Ingreso manual" (`btnManualEntry`)'
                    form={form}
                    placeholder="Ingreso Manual"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'help']}
                    label="Texto ayuda (`help`)"
                    form={form}
                    placeholder="default: En blanco"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'btnLogin']}
                    label='Botón "Login" (`btnLogin`)'
                    form={form}
                    placeholder="Login"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'pickupPrompt']}
                    label="Indicación para pantalla de entrega (`pickupPrompt`)"
                    form={form}
                    placeholder="Ingresa el código que recibiste para retirar tu paquete."
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'pickupScanPrompt']}
                    label="Indicación para escanear en pantalla de entrega (`pickupScanPrompt`)"
                    form={form}
                    placeholder="Puedes escanear tu código en el lector en cualquier momento."
                  />
                  {form.getValue(['preferences', 'pickupNationalIdEnabled']) ? (
                    <Fragment>
                      <Input
                        readOnly={readOnly}
                        name={['preferences', 'nationalIdPromptPretitle']}
                        label="Indicación para pantalla de entrega (`nationalIdPromptPretitle`)"
                        form={form}
                        placeholder="Último paso:"
                      />
                      <Input
                        readOnly={readOnly}
                        name={['preferences', 'nationalIdPropmtTitle']}
                        label="Indicación para escanear en pantalla de entrega (`nationalIdPropmtTitle`)"
                        form={form}
                        placeholder="Escanea el código de tu cédula de identidad en el lector."
                      />
                    </Fragment>
                  ) : null}
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'supportTitle']}
                    label="Título de soporte"
                    form={form}
                    placeholder="Necesitas ayuda? (`supportTitle`)"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'supportBody']}
                    label="Texto de soporte (`supportBody`)"
                    form={form}
                    placeholder="Si necesitas ayuda contáctanos <br /> al <strong>{{supportPhone}}</strong> o a <strong>{{supportEmail}}</strong>."
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'offlineTitle']}
                    label="Título de estado offline (`offlineTitle`)"
                    form={form}
                    placeholder="Temporalmente sin conexión"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'offlineBody']}
                    label="Texto de estado offline (`offlineBody`)"
                    form={form}
                    placeholder="{{> supportBody }}"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'maintenanceTitle']}
                    label="Título de en mantención (`maintenanceTitle`)"
                    form={form}
                    placeholder="Locker en mantención"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'maintenanceBody']}
                    label="Texto de en mantención (`maintenanceBody`)"
                    form={form}
                    placeholder="{{> supportBody }}"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'outOfServiceTitle']}
                    label="Título de fuera de servicio (`outOfServiceTitle`)"
                    form={form}
                    placeholder="Temporalmente fuera de servicio"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'outOfServiceBody']}
                    label="Texto de fuera de servicio (`outOfServiceBody`)"
                    form={form}
                    placeholder="{{> supportBody }}"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'frozenTitle']}
                    label="Título de bloqueo (`frozenTitle`)"
                    form={form}
                    placeholder="Demasiados intentos fallidos"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'frozenBody']}
                    label="Texto de bloqueo (`frozenBody`)"
                    form={form}
                    placeholder="Por seguridad debes esperar antes de seguir intentándolo."
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'thankyou']}
                    label="Texto de fin de transacción (`thankyou`)"
                    form={form}
                    placeholder="Gracias por usar eLocker {{name}}."
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'packageName']}
                    label="Nombre del paquete (`packageName`)"
                    form={form}
                    placeholder="tu paquete"
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'wrongCode']}
                    label="Texto de código inválido (`wrongCode`)"
                    form={form}
                    placeholder="Código inválido, por favor intenta de nuevo."
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'openDoors']}
                    label="Texto de puerta abierta (`openDoors`)"
                    form={form}
                    placeholder="Ya puedes retirar tu paquete. <br/><small>Por favor cierra la puerta para terminar.</small>"
                  />
                </Fragment>
              ) : null}
              <h3 className={css.panelSubtitle}>Colores</h3>
              <Toggle name={['preferences', 'customizeColors']} label="Personalizar colores?" form={form} />
              {form.getValue(['preferences', 'customizeColors']) ? (
                <Fragment>
                  <Input readOnly={readOnly} name={['preferences', 'textColor']} label="Color de Texto" form={form} />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'adminTextColor']}
                    label="Color de texto de área de administración"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'backgroundColor']}
                    label="Color de Fondo"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'adminBackgroundColor']}
                    label="Color de fondo de área administración"
                    form={form}
                  />
                  <h4 className={css.panelSubsection}>Botones</h4>
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'buttonTextColor']}
                    label="Color de texto de botones"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'buttonColor']}
                    label="Color de fondo de botones"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'primaryButtonBackground']}
                    label="Color de fondo de botón primario"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'buttonBorderColor']}
                    label="Color de borde de botones"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'buttonActiveColor']}
                    label="Color de fondo de botones en estado activo"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'scannerArrowFillColor']}
                    label="Color de flecha del escáner"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'scannerFillColor']}
                    label="Color de relleno del escáner"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'scannerCodeFillColor']}
                    label="Color de código del escáner"
                    form={form}
                  />
                  <Input
                    readOnly={readOnly}
                    name={['preferences', 'scannerBackgroundFill']}
                    label="Color de fondo del escáner"
                    form={form}
                  />
                </Fragment>
              ) : null}
            </div>
          ) : null}
        </section>
        <section className={grid.half}>
          <div className={classnames(css.formPanel, css.doors)}>
            <h2 className={css.panelTitle}>Puertas disponibles</h2>
            <div className={css.wrapper}>
              {Object.entries(doorsBySize).map(([key, doorArray], index) => (
                <Count
                  value={getActive(doorArray)}
                  valueLabel={getSize(index)}
                  name={key.replace(/ /g, '')}
                  total={doorArray.length}
                  percentage
                  miniBar
                  width={130}
                  key={`door-${index}`}
                />
              ))}
            </div>
            <DoorList locker={item} lockerActions={actions} orders={lockerOrders} readOnly={readOnly} />
            <h3 className={css.panelSubtitle}>Extras</h3>
            <TextArea name="extras" type="json" label="Extras" form={form} readOnly={readOnly} />
          </div>
        </section>
      </div>
    </main>
  );
};

export default Locker;
